import { session, local } from 'store2';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

import {
    Configuration,
    AuthApi,
    AccountsApi,
    AccountsApiInterface,
    ReportsApi,
    ReportsApiInterface,
    CampaignsApi,
    AuthApiInterface,
    CampaignsApiInterface,
    TemplatesApiInterface,
    TemplatesApi,
    EmailsApi,
    EmailsApiInterface,
    FilesApi,
    FilesApiInterface,
    DomainsApi,
    DomainsApiInterface,
    ValidEmailsApi,
    ValidEmailsApiInterface,
    SecurityApi,
    SecurityApiInterface,
    ContactsApi,
    ContactsApiInterface,
    PoolsApi,
    PoolsApiInterface,
    InboundRouteApi,
    InboundRouteApiInterface,
    WebhooksApi,
    WebhooksApiInterface,
    ChannelsApiInterface,
    ChannelsApi,
    TasksApi,
    TasksApiInterface,
    ListsApi,
    ListsApiInterface,
    SegmentsApi,
    SegmentsApiInterface,
    SubAccountsApi,
    SubAccountsApiInterface,
    ProductApiInterface,
    ProductApi,
    NotificationApi,
    NotificationApiInterface,
    UsersApi,
    UsersApiInterface,
    FormsApiInterface,
    FormsApi,
    PaymentsApi,
    PaymentsApiInterface,
    IntegrationApi,
    IntegrationApiInterface,
    AItoolsApi,
    AItoolsApiInterface,
    MergeFieldApi,
    MergeFieldApiInterface,
    LandingsApi,
    LandingsApiInterface,
    CheckoutsApi,
    CheckoutsApiInterface,
} from '@dev/ee-api';
import { identity, pickBy } from 'lodash-es';
import { store } from '@shared-store/shared-store';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import Authentication from '../Authentication';
import { StorageKeysEnum } from '../dictionaries/StorageKeys';

declare const ee: any;
declare const window: any;

interface ConfParams {
    basePath: string;
    apiKey?: string;
    baseOptions?: any;
    headers?: any;
}

interface RequestInterface {
    Accounts: AccountsApiInterface;
    Auth: AuthApiInterface;
    Statistics: ReportsApiInterface;
    Channels: ChannelsApiInterface;
    Campaigns: CampaignsApiInterface;
    Templates: TemplatesApiInterface;
    Checkouts: CheckoutsApiInterface;
    Forms: FormsApiInterface;
    Emails: EmailsApiInterface;
    Files: FilesApiInterface;
    Domain: DomainsApiInterface;
    ValidEmails: ValidEmailsApiInterface;
    Security: SecurityApiInterface;
    Contacts: ContactsApiInterface;
    Pools: PoolsApiInterface;
    Inbound: InboundRouteApiInterface;
    Webhooks: WebhooksApiInterface;
    Tasks: TasksApiInterface;
    Activity: ReportsApiInterface;
    Reports: ReportsApiInterface;
    Lists: ListsApiInterface;
    Segments: SegmentsApiInterface;
    Domains: DomainsApiInterface;
    SubAccounts: SubAccountsApiInterface;
    Products: ProductApiInterface;
    Notification: NotificationApiInterface;
    Users: UsersApiInterface;
    Payments: PaymentsApiInterface;
    Integration: IntegrationApiInterface;
    AiTools: AItoolsApiInterface;
    MergeField: MergeFieldApiInterface;
    Landings: LandingsApiInterface;
}

interface AdditionalRequestOptions {
    disableImpersonation?: boolean;
    onUploadProgress?: Record<string, any>;
}

export type RequestOptions = AxiosRequestConfig & AdditionalRequestOptions;

const logoutFromApp = (): void => {
    Authentication.removeAccessToken();

    if (store.getState().config?.bootedAsChild) {
        const path = `${location.origin}/login`;
        window.location.href = path;
    } else {
        window.location.reload();
    }
};

/**
 * Method used for request call configuration, returns category api handlers
 */
export const request = (options: RequestOptions = {}): RequestInterface => {
    const getApiPath = (): string => {
        if (!store?.getState()?.config?.isStandalone && window.ee) {
            return ee.api.path;
        }

        return APIPATH || session.get('settings').apiPath;
    };

    const apiPath = getApiPath();
    const availableHeaders = {
        'X-ElasticEmail-ImpersonateAs': !options.disableImpersonation && session.get('subaccountemailtouse'),
        'X-ElasticEmail-BrowserToken': local.get('browserToken'),
        'X-ElasticEmail-Fingerprint': !session.get(StorageKeysEnum.SubaccountEmailToUse)
            ? local.get(StorageKeysEnum.Fingerprint)
            : undefined,
    };

    const addedHeaders = pickBy(availableHeaders, identity);

    const getParams = (): ConfParams => {
        const baseOptions = {
            ...{
                timeout: 60000,
            },
            ...options,
        };

        const params: ConfParams = {
            basePath: `${apiPath}/v3`,
            baseOptions,
            headers: addedHeaders,
        };

        if (Authentication.isAuthenticated()) {
            params.apiKey = Authentication.getAccessToken();
        }

        return params;
    };
    const conf = new Configuration(getParams());

    const client: AxiosInstance = axios.create();

    /**
     * adding our headers to every request
     */
    client.interceptors.request.use((config) => {
        config.headers = {
            ...config.headers,
            ...addedHeaders,
        } as AxiosRequestHeaders;
        return config;
    });

    client.interceptors.response.use(undefined, (err): any => {
        if (err.code === 'ECONNABORTED') {
            const showDialogParams = {
                title: 'Connection timeout',
                body: 'Looks like the server is taking to long to respond, this can be caused by either poor connectivity or an error with our servers. Please try again in a while.',

                confirmLabel: 'Ok',
            };

            console.error(
                `connection timeout -> url: ${err.config.url}, method: ${err.config.method}, timeout: ${err.config.timeout}ms`
            );

            store.dispatch(ShowDialog(showDialogParams));
            return Promise.reject(err);
        }

        if (err?.response?.status === 410) {
            const showDialogParams: ShowDialogParams = {
                title: 'Your Session Has Expired',
                body: 'Your session has expired and you must log in again.',
                onClickConfirm: logoutFromApp,
                confirmLabel: 'Ok',
                onClose: logoutFromApp,
                onClickCancel: logoutFromApp,
            };

            store.dispatch(ShowDialog(showDialogParams));
            return Promise.reject(err);
        }

        return Promise.reject(err);
    });

    const Accounts = new AccountsApi(conf, null, client);
    const Auth = new AuthApi(conf, null, client);
    const Statistics = new ReportsApi(conf, null, client);
    const Channels = new ChannelsApi(conf, null, client);
    const Campaigns = new CampaignsApi(conf, null, client);
    const Templates = new TemplatesApi(conf, null, client);
    const Emails = new EmailsApi(conf, null, client);
    const Files = new FilesApi(conf, null, client);
    const Domain = new DomainsApi(conf, null, client);
    const ValidEmails = new ValidEmailsApi(conf, null, client);
    const Security = new SecurityApi(conf, null, client);
    const Contacts = new ContactsApi(conf, null, client);
    const Pools = new PoolsApi(conf, null, client);
    const Inbound = new InboundRouteApi(conf, null, client);
    const Webhooks = new WebhooksApi(conf, null, client);
    const Tasks = new TasksApi(conf, null, client);
    const Activity = new ReportsApi(conf, null, client);
    const Lists = new ListsApi(conf, null, client);
    const Segments = new SegmentsApi(conf, null, client);
    const Domains = new DomainsApi(conf, null, client);
    const SubAccounts = new SubAccountsApi(conf, null, client);
    const Products = new ProductApi(conf, null, client);
    const Notification = new NotificationApi(conf, null, client);
    const Users = new UsersApi(conf, null, client);
    const Forms = new FormsApi(conf, null, client);
    const Payments = new PaymentsApi(conf, null, client);
    const Integration = new IntegrationApi(conf, null, client);
    const AiTools = new AItoolsApi(conf, null, client);
    const MergeField = new MergeFieldApi(conf, null, client);
    const Landings = new LandingsApi(conf, null, client);
    const Checkouts = new CheckoutsApi(conf, null, client);

    return {
        Activity,
        Accounts,
        Statistics,
        Channels,
        Campaigns,
        Auth,
        Templates,
        Emails,
        Files,
        Security,
        Domain,
        ValidEmails,
        Contacts,
        Segments,
        Lists,
        Pools,
        Inbound,
        Webhooks,
        Tasks,
        Reports: Activity,
        Domains,
        SubAccounts,
        Products,
        Notification,
        Users,
        Forms,
        Payments,
        Integration,
        AiTools,
        MergeField,
        Landings,
        Checkouts,
    };
};
